import { useCallback, useEffect, useMemo, useState } from 'react'

import { Button } from 'core-components/library/button'
import { Loader } from 'core-components/library/loader'
import { ModalDialog } from 'core-components/library/modalDialog'
import { Tooltip } from 'core-components/library/tooltip'
import { Crud } from 'core/types/Crud'
import { ThirdPartyLogApplication } from 'onboarding/classes/models/thirdPartyLog'
import { fail } from 'onboarding/classes/services/notification'

import { ThirdPartyLogList } from './ThirdPartyLogList'
import { TAdditionalApplicationSourceSettings } from './types'

import styles from './ThirdPartyLogButton.module.scss'

interface IProps {
  caption: string
  createApplicationLogsCrud: (application: ThirdPartyLogApplication) => Crud
  fetchApplications: () => Promise<ThirdPartyLogApplication[]>
  isDisabled?: boolean
  additionalApplicationSourceSettings?: TAdditionalApplicationSourceSettings
}

export const ThirdPartyLogButton = ({
  caption,
  createApplicationLogsCrud,
  fetchApplications,
  isDisabled,
  additionalApplicationSourceSettings,
}: IProps) => {
  const [isApplicationsLoading, setIsApplicationsLoading] = useState(false)
  const [applications, setApplications] = useState<ThirdPartyLogApplication[]>([])
  const [showDialog, setShowDialog] = useState(false)

  const isAnyLog = Boolean(applications.length)

  const modalContent = useMemo(() => {
    const fallback = <div>There are no logs...</div>

    if (isApplicationsLoading) {
      return <Loader />
    }

    if (isAnyLog) {
      return (
        <ThirdPartyLogList
          applications={applications}
          createApplicationLogsCrud={createApplicationLogsCrud}
          additionalApplicationSourceSettings={additionalApplicationSourceSettings}
        />
      )
    }

    return fallback
  }, [
    isApplicationsLoading,
    isAnyLog,
    createApplicationLogsCrud,
    additionalApplicationSourceSettings,
    applications,
  ])

  const onClose = useCallback(() => {
    setShowDialog(false)
  }, [])

  useEffect(() => {
    const fetch = async () => {
      try {
        setIsApplicationsLoading(true)
        const applications = await fetchApplications()
        setApplications(applications)
      } catch (e: any) {
        fail(e.message)
      } finally {
        setIsApplicationsLoading(false)
      }
    }

    fetch()
  }, [fetchApplications])

  return (
    <div className={styles.header}>
      <Tooltip tipContent={() => <span>No third party logs</span>} disabled={isAnyLog}>
        <Button
          className={styles.actionsItem}
          onClick={() => setShowDialog(true)}
          disabled={isDisabled || !isAnyLog}
          loading={isApplicationsLoading}
        >
          {caption}
        </Button>
      </Tooltip>

      <ModalDialog size={'md'} show={showDialog} onClose={onClose} content={() => modalContent} />
    </div>
  )
}
